import React, { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import Router from 'next/router'
import { useSession } from 'next-auth/react'

import { useAppSelector } from '#/app/store/store'
import ExclamationOutlineSvg from '#/assets/svg/exclamation-outline.svg?react'
import { LOW_BALANCE_OFFER, getModalById, PlateTemplate } from '#/features/modals'
import { c } from '#/shared'
import { accountApi } from '#/shared/api/account-endpoints'
import { formatDate } from '#/shared/lib/helpers/date-helper'
import { commaSeparated, getDeviceType } from '#/shared/lib/helpers'
import { IOffer } from '#/views/subscription'
import Offer from '#/views/subscription/ui/offer'

import styles from './low-balance-offer-plate.module.scss'

function findUpgradeOffer(offers: IOffer[], currentUid: string): IOffer | null {
	const current = offers.find((o) => o.uid === currentUid)
	if (!current) return null
	const currentPrice = Number(current.price)
	const sorted = [...offers].sort((a, b) => Number(a.price) - Number(b.price))
	return sorted.find((o) => Number(o.price) > currentPrice) ?? null
}

function formatAccessDate(iso: string | null | undefined): string | null {
	if (!iso?.trim()) return null
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return null
	return formatDate(d, { day: 'numeric', month: 'long' })
}

export const LowBalanceOfferPlate = () => {
	const modal = getModalById(LOW_BALANCE_OFFER)
	const { data } = useSession()
	const [offers, setOffers] = useState<IOffer[] | null>(null)
	const [paymentFailed, setPaymentFailed] = useState(false)
	const [isPaying, setIsPaying] = useState(false)

	const plan = useAppSelector((state) => state.user.payment_plan.plan)
	const nextPaymentAt = useAppSelector((state) => state.user.payment_plan.next_payment_at)

	const hasSubscription = Number(plan.price) > 0
	const currentPlanUid = plan.uid
	const currentPlanPrice = plan.price
	const currentPlanTitle = plan.title || 'текущий'

	useEffect(() => {
		if (data?.access) {
			accountApi.getPaymentsPlans(data.access).then((res) => {
				if (res) setOffers(res)
			})
		}
	}, [data?.access])

	useEffect(() => {
		if (modal.state) {
			setPaymentFailed(false)
		}
	}, [modal.state])

	const handleClose = () => {
		setPaymentFailed(false)
		modal.setState(false)
	}

	const offerIndex = modal.getStoreProperty<number>('offerIndex') ?? 1
	const offerUid = modal.getStoreProperty<string>('offerUid')
	const notEnoughtTokens = modal.getStoreProperty<boolean>('notEnoughtTokens')

	const recommendedOffer = useMemo(() => {
		if (!offers) return null
		if (offerUid) return offers.find((o) => o.uid === offerUid) ?? null
		return offers[offerIndex] ?? offers[1] ?? null
	}, [offers, offerUid, offerIndex])

	const upgradeOffer = useMemo(() => {
		if (!offers || !currentPlanUid) return null
		return findUpgradeOffer(offers, currentPlanUid)
	}, [offers, currentPlanUid])

	const renewPriceLabel = commaSeparated(parseInt(currentPlanPrice, 10) || 0)
	const accessDateLabel = formatAccessDate(nextPaymentAt)
	const renewPriceFromOffer = offers?.find((o) => o.uid === currentPlanUid)?.price ?? currentPlanPrice

	const payPlan = async (planUid: string) => {
		if (!data?.access || !planUid || isPaying) return
		setIsPaying(true)
		try {
			const urlForPay = await accountApi.payProduct(data.access, planUid)
			if (urlForPay) {
				await Router.push(urlForPay)
			}
		} catch {
			setPaymentFailed(true)
		} finally {
			setIsPaying(false)
		}
	}

	const handlePayError = () => {
		setPaymentFailed(true)
	}

	if (paymentFailed) {
		const failPrice = commaSeparated(parseInt(String(renewPriceFromOffer), 10) || 0)
		return (
			<PlateTemplate id={LOW_BALANCE_OFFER}>
				<div className={c(styles.container, styles.failContainer)} onClick={(e) => e.stopPropagation()}>
					<div className={styles.failIconWrap}>
						<ExclamationOutlineSvg width={29} height={29} color="#EDC948" />
					</div>
					<p className={styles.failHeading}>Не получилось продлить подписку</p>
					<p className={styles.failText}>
						Платеж по карте не прошел. Доступ к тарифу <b>{currentPlanTitle}</b>
						{accessDateLabel ? (
							<>
								{' '}
								сохранен до <b>{accessDateLabel}</b>
							</>
						) : null}{' '}
						— продлите оптимальным способом.
					</p>
					<button
						type="button"
						className={styles.primaryBtn}
						disabled={isPaying || !currentPlanUid}
						onClick={() => payPlan(currentPlanUid)}
					>
						Оплатить
					</button>
					<div className={styles.failFooter}>
						<span>{failPrice}₽ за 30 дней</span>
						<span>Отмена в любой момент</span>
					</div>
				</div>
			</PlateTemplate>
		)
	}

	return (
		<PlateTemplate id={LOW_BALANCE_OFFER}>
			<div className={c(styles.container)} onClick={(e) => e.stopPropagation()}>
				{notEnoughtTokens ? (
					<p className={styles.text_heading}>
						У вас не осталось
						<br />
						токенов...
					</p>
				) : (
					<p className={styles.text_heading}>
						Осталось
						<br />
						мало токенов...
					</p>
				)}
				<p className={styles.text}>
					Подобрали для Вас наиболее подходящий тариф,
					<br />
					взгляните на преимущества!
				</p>

				{hasSubscription ? (
					<>
						{offers === null ? (
							<p className={styles.text}>Загрузка тарифов...</p>
						) : (
							<>
								{upgradeOffer ? (
									<div className={styles.offersWrapper}>
										<Offer
											key={upgradeOffer.uid}
											{...upgradeOffer}
											device={getDeviceType()}
											isCurrentSubscription={false}
											index={1}
											ctaLabel="Улучшить подписку"
											onPayError={handlePayError}
										/>
									</div>
								) : null}
								{upgradeOffer ? <p className={styles.orDivider}>или</p> : null}
								<button
									type="button"
									className={styles.secondaryBtn}
									disabled={isPaying || !currentPlanUid}
									onClick={() => payPlan(currentPlanUid)}
								>
									Обновить подписку за {renewPriceLabel} ₽
								</button>
							</>
						)}
					</>
				) : (
					<>
						<div className={styles.offersWrapper}>
							{offers === null ? (
								<p className={styles.text}>Загрузка тарифов...</p>
							) : recommendedOffer ? (
								<Offer
									key={recommendedOffer.uid}
									{...recommendedOffer}
									device={getDeviceType()}
									isCurrentSubscription={false}
									index={offerIndex}
									ctaLabel={`Оплатить за ${commaSeparated(parseInt(recommendedOffer.price, 10) || 0)} ₽`}
									onPayError={handlePayError}
								/>
							) : null}
						</div>
						<Link className={styles.link} href="/subscription" onClick={handleClose}>
							<p>Нет, я хочу посмотреть все тарифы</p>
						</Link>
					</>
				)}
			</div>
		</PlateTemplate>
	)
}
